/*
 * KINDW-195 — Payment amount (Full vs 25% Deposit) eligibility + math.
 *
 * Pure helpers, no React, no SDK hooks. Component / context callers pass in
 * the basket, a `productsById` lookup, and the shopper-scoped warehouse
 * inventory ids so we can resolve ATS per warehouse (IM-004) without
 * re-querying SCAPI here.
 *
 * Source-of-truth for:
 *   - which basket attribute persists the shopper's choice
 *     (`c_paymentAmountType`),
 *   - which basket attribute persists the value to charge (`c_paymentAmount`),
 *   - which product/payment-method flag opts an item OUT of deposit
 *     (`c_isExcludedFromDeposit`),
 *   - the deposit rate from MRT `depositPercentage` and matching display copy
 *     ("7 days before delivery"),
 *   - the "in-stock product" definition the Payment Amount section uses
 *     (ATS > 0 AND ATS !== 999999) and the "main / visible product" scope it
 *     evaluates that on (via `groupComplexLineItems().visibleItems`).
 */

import {getProductAts} from '@amblique/king-living/app/utils/inventory-utils';
import {groupComplexLineItems} from '@amblique/king-living/app/utils/cart-utils';
import {getProductLeadTimeDays} from '@amblique/king-living/app/utils/product-utils';

/**
 * Parse SFCC/MRT `depositPercentage` (e.g. `25` = 25%) for deposit math and UI.
 *
 * @param {*} value - Raw preference from `mrtClientData.preferences.depositPercentage`
 * @returns {{
 *   depositPercentage: number|null,
 *   depositRate: number|null,
 *   isDepositPercentageValid: boolean
 * }}
 */
export const parseDepositPercentage = (value) => {
  if (value == null || value === '') {
    return {depositPercentage: null, depositRate: null, isDepositPercentageValid: false};
  }

  let numeric = typeof value === 'number' ? value : Number(String(value).trim());

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return {depositPercentage: null, depositRate: null, isDepositPercentageValid: false};
  }

  // SFCC site pref is an int (e.g. 25 = 25%). Some payloads may send a decimal rate (0.25).
  if (numeric > 0 && numeric <= 1) {
    numeric *= 100;
  }

  if (numeric > 100) {
    return {depositPercentage: null, depositRate: null, isDepositPercentageValid: false};
  }

  return {
    depositPercentage: numeric,
    depositRate: numeric / 100,
    isDepositPercentageValid: true,
  };
};

/**
 * Localized label for deposit copy (e.g. "30%") from BM/MRT integer percent.
 *
 * @param {number} depositPercentage - Whole percent from site preference (e.g. 30)
 * @param {import('react-intl').IntlShape} intl
 * @returns {string|null}
 */
export const formatDepositPercentageLabel = (depositPercentage, intl) => {
  if (!Number.isFinite(depositPercentage) || depositPercentage <= 0 || depositPercentage > 100) {
    return null;
  }

  return intl.formatNumber(depositPercentage / 100, {
    style: 'percent',
    maximumFractionDigits: Number.isInteger(depositPercentage) ? 0 : 2,
  });
};

/**
 * @param {number|null|undefined} depositRate - Decimal rate (e.g. `0.25` for 25%)
 * @returns {boolean}
 */
const isDepositRateValid = (depositRate) => Number.isFinite(depositRate) && depositRate > 0 && depositRate <= 1;

/**
 * SCAPI "orderable but no real on-hand inventory" sentinel. SFCC inventory
 * lists use this value (and only this value) for SKUs that should always be
 * orderable but have no warehouse stock — typically backorder / made-to-order
 * products fronted by SAP. For Payment Amount visibility this MUST NOT count
 * as "in-stock" (FSD PAYMENT-01 #1 only hides the section when at least one
 * line item really is on-hand and therefore must be paid in full).
 */
export const IN_STOCK_SENTINEL_ATS = 999999;

/**
 * Payment-amount enum + the basket attribute key it persists into.
 * `null` is the third valid value at the React layer (= shopper hasn't
 * picked yet); SCAPI receives a real string only after a selection.
 */
export const PAYMENT_AMOUNT_TYPE = Object.freeze({
  FULL: 'full',
  DEPOSIT: 'deposit',
});

export const PAYMENT_AMOUNT_TYPE_ATTR = 'c_paymentAmountType';

/**
 * Basket / Order attribute that stores the monetary amount the shopper will
 * be charged at place-order (full order total when `paymentAmountType` is
 * `'full'`, the deposit pay-now figure when `'deposit'`). Persisted so the
 * OMS can read the agreed amount post-placement and process the remaining
 * balance later without re-running the checkout-time math.
 */
export const PAYMENT_AMOUNT_ATTR = 'c_paymentAmount';

/**
 * Product- and PaymentMethod-level "exclude from deposit" flag.
 *
 * The PWA treats `undefined` as `false` (i.e. eligible by default) so the
 * UI keeps working before the matching custom-attribute migration lands in
 * `system-objecttype-extensions.xml`. Once the SFRA PR ships, BM editors
 * tick this on the items that must be paid in full (e.g. floor-stock /
 * clearance / commission pieces).
 */
export const EXCLUDED_FROM_DEPOSIT_ATTR = 'c_isExcludedFromDeposit';

/**
 * Classification mode returned by `classifyBasket`. "Ineligible" means a line
 * item is either flagged `c_isExcludedFromDeposit` OR (KL-1044) is an in-stock
 * floor-stock item — both must be paid in full.
 *   - 'no-items'         basket empty / not yet loaded
 *   - 'all-eligible'     every line item can be deposited
 *   - 'mixed'            both eligible and ineligible items present
 *   - 'all-ineligible'   every line item is opt-out (deposit hidden + auto-full)
 */
export const DEPOSIT_BASKET_MODE = Object.freeze({
  NO_ITEMS: 'no-items',
  ALL_ELIGIBLE: 'all-eligible',
  MIXED: 'mixed',
  ALL_INELIGIBLE: 'all-ineligible',
});

/**
 * Resolve the "main / visible" basket line items the Payment Amount logic
 * cares about. Children of complex/configured products (cushions, set
 * additions, recommendation companions) are hidden under their parent in
 * the cart flyout, checkout order summary, and confirmation surfaces, so
 * the in-stock check (FSD PAYMENT-01 #1.2) only inspects the parent.
 *
 * Falls back to the raw `productItems` array when grouping yields nothing
 * (defensive against partial baskets / unit-test fixtures that don't carry
 * UUID parent links).
 *
 * @param {Object} basket
 * @returns {Array} visible/main line items
 */
const getMainVisibleItems = (basket) => {
  const items = Array.isArray(basket?.productItems) ? basket.productItems : [];

  if (items.length === 0) return [];

  const grouped = groupComplexLineItems(items);

  return Array.isArray(grouped?.visibleItems) && grouped.visibleItems.length > 0 ?
    grouped.visibleItems :
    items;
};

/**
 * Is this product genuinely "in stock" for the Payment Amount visibility
 * rule? Per the latest FSD PAYMENT-01 #1.2 update:
 *
 *   In-stock = ATS > 0 AND ATS !== 999999
 *
 * The `999999` sentinel comes from SFCC inventory lists for orderable SKUs
 * that have no real on-hand inventory (typically MTO / backorder fronted by
 * SAP). Those must NOT lock the shopper into full-pay because there is no
 * floor-stock unit waiting on a shelf.
 *
 * Returns `false` for any product without resolvable inventory (`null` ATS)
 * so a misconfigured product can't accidentally hide the deposit option.
 *
 * @param {Object|null|undefined} product - SCAPI product
 * @param {string|undefined|null} [inventoryIds] - Same `inventoryIds` cookie passed to SCAPI
 * @returns {boolean}
 */
export const isProductInStock = (product, inventoryIds = null) => {
  const ats = getProductAts(product, inventoryIds);

  if (ats === null || ats === undefined) return false;
  if (!Number.isFinite(ats)) return false;
  if (ats <= 0) return false;
  if (ats === IN_STOCK_SENTINEL_ATS) return false;

  return true;
};

/**
 * Does the basket contain at least one **in-stock main product**?
 *
 * FSD PAYMENT-01 #1.2 — when this is true we hide the Payment Amount
 * section outright and force `'full'`. "Main" = the visible parent rows in
 * the cart / checkout summary / confirmation; children of complex products
 * are not inspected because they inherit the parent's purchasing mode.
 *
 * Falls back to `false` when `productsById` is missing (lookup hasn't
 * resolved yet) — the provider re-runs once the products query settles.
 *
 * @param {Object} basket - SCAPI basket
 * @param {Object|null} [productsById] - Map of `productId → product` (with `inventory`)
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {boolean}
 */
export const hasInStockItems = (basket, productsById = null, inventoryIds = null) => {
  if (!productsById) return false;

  const items = getMainVisibleItems(basket);

  if (items.length === 0) return false;

  return items.some((item) => {
    const product = item?.productId ? productsById[item.productId] : null;

    return isProductInStock(product, inventoryIds);
  });
};

/**
 * Product lead time in days from the SFCC `leadTime` custom attribute
 * (`c_leadTime` in SCAPI). Used for DELIVERY-01 mixed-cart messaging.
 *
 * @param {Object|null|undefined} product
 * @returns {number}
 */
export {getProductLeadTimeDays} from '@amblique/king-living/app/utils/product-utils';

/**
 * Is this product custom-made / made-to-order for checkout delivery warnings
 * (DELIVERY-01)? True when the product carries an explicit lead time or uses
 * the SFCC orderable-without-floor-stock ATS sentinel (999999).
 *
 * @param {Object|null|undefined} product - SCAPI product
 * @param {string|undefined|null} [inventoryIds] - Same `inventoryIds` cookie passed to SCAPI
 * @returns {boolean}
 */
export const isProductCustomMade = (product, inventoryIds = null) => {
  if (!product) return false;

  if (getProductLeadTimeDays(product) > 0) return true;

  const ats = getProductAts(product, inventoryIds);

  return ats === IN_STOCK_SENTINEL_ATS;
};

/**
 * Does the basket contain at least one custom-made main product?
 *
 * @param {Object} basket - SCAPI basket
 * @param {Object|null} [productsById] - Map of `productId → product`
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {boolean}
 */
export const hasCustomMadeItems = (basket, productsById = null, inventoryIds = null) => {
  if (!productsById) return false;

  const items = getMainVisibleItems(basket);

  if (items.length === 0) return false;

  return items.some((item) => {
    const product = item?.productId ? productsById[item.productId] : null;

    return isProductCustomMade(product, inventoryIds);
  });
};

/**
 * DELIVERY-01 (KINDW-1692) — does the basket contain both an in-stock main
 * product and a custom-made main product? Requires `productsById` so we do
 * not flash the warning before the shared checkout products query settles.
 *
 * @param {Object} basket - SCAPI basket
 * @param {Object|null} [productsById] - Map of `productId → product`
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {boolean}
 */
export const hasMixedCheckoutCart = (basket, productsById = null, inventoryIds = null) => {
  if (!productsById) return false;

  const items = getMainVisibleItems(basket);

  if (items.length < 2) return false;

  return (
    hasInStockItems(basket, productsById, inventoryIds) &&
    hasCustomMadeItems(basket, productsById, inventoryIds)
  );
};

/**
 * Resolve the deposit-eligibility flag for a single basket line item.
 *
 * Looks in four places, in order of precedence:
 *   1. The line item itself (`productItem.c_isExcludedFromDeposit`) — lets
 *      future BM logic stamp a per-line override without re-fetching the
 *      product.
 *   2. The product object from the `productsById` lookup if present.
 *   3. KL-1044 — the product's live stock: an **in-stock** (floor-stock) item
 *      is itself NOT eligible for deposit. Per the client rule an in-stock item
 *      behaves exactly like a `c_isExcludedFromDeposit` item — it must be paid
 *      in full — but it does NOT hide the Payment Amount section: in a mixed
 *      basket the deposit option still shows and the in-stock item's full price
 *      is folded into the "pay now" total (see `calculatePayNowOnDeposit`).
 *      Only evaluated when the product (and, when needed, `inventoryIds`)
 *      resolves; without product context we fall back to flag-only eligibility
 *      so the UI stays optimistic until inventory settles.
 *   4. Default → eligible (deposit allowed).
 *
 * For complex products only the **main (visible)** line is consulted —
 * children of a configured product inherit the parent's eligibility, which
 * matches the way the shopper sees the product in the cart / checkout
 * order summary (one row per "product").
 *
 * @param {Object} productItem - basket.productItems entry
 * @param {Object} [productsById] - optional map of productId → product
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {boolean} true if the item can be deposited
 */
export const isItemDepositEligible = (productItem, productsById = null, inventoryIds = null) => {
  if (productItem?.[EXCLUDED_FROM_DEPOSIT_ATTR] === true) return false;

  const id = productItem?.productId;
  const product = productsById && id ? productsById[id] : null;

  if (product?.[EXCLUDED_FROM_DEPOSIT_ATTR] === true) return false;

  // KL-1044 — in-stock items are treated the same as "not eligible for deposit".
  if (product && isProductInStock(product, inventoryIds)) return false;

  return true;
};

/**
 * Classify a basket into the four deposit-eligibility modes and return the
 * eligible / ineligible **main-product** splits. UI consumers use `mode`
 * to decide which radio state to render and the splits to compute
 * monetary totals.
 *
 * Iterates the grouped/visible main products (same set rendered in the
 * cart flyout, checkout order summary and confirmation), NOT the raw
 * `basket.productItems[]`. This matters in two ways:
 *
 *   1. **Classification scope.** A complex product (e.g. Myco Side Table
 *      with a Cover variant + 2 Timbers + Base + KingGuard option) appears
 *      to the shopper as ONE product line. The deposit eligibility of the
 *      whole group is decided by the parent (visible) line — a single
 *      hidden child whose product master carries `c_isExcludedFromDeposit`
 *      no longer drags the entire basket into "mixed" mode against the
 *      shopper's mental model.
 *   2. **Pricing scope.** The grouped visible items already carry
 *      aggregated `priceAfterItemDiscount` / `price` values that include
 *      the parent + every child line + every `optionItems[].price` (e.g.
 *      KingGuard $50). That means `sumItemsSubtotal(eligibleItems)`
 *      reconciles with `basket.productSubTotal`, instead of silently
 *      missing the option contributions that don't live on
 *      `productItem.priceAfterItemDiscount`.
 *
 * @param {Object} basket
 * @param {Object} [productsById]
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {{
 *   mode: typeof DEPOSIT_BASKET_MODE[keyof typeof DEPOSIT_BASKET_MODE],
 *   eligibleItems: Array,
 *   ineligibleItems: Array
 * }}
 */
export const classifyBasket = (basket, productsById = null, inventoryIds = null) => {
  const items = getMainVisibleItems(basket);

  if (items.length === 0) {
    return {mode: DEPOSIT_BASKET_MODE.NO_ITEMS, eligibleItems: [], ineligibleItems: []};
  }

  const eligibleItems = [];
  const ineligibleItems = [];

  items.forEach((item) => {
    if (isItemDepositEligible(item, productsById, inventoryIds)) {
      eligibleItems.push(item);
    } else {
      ineligibleItems.push(item);
    }
  });

  let mode = DEPOSIT_BASKET_MODE.ALL_ELIGIBLE;

  if (eligibleItems.length === 0) {
    mode = DEPOSIT_BASKET_MODE.ALL_INELIGIBLE;
  } else if (ineligibleItems.length > 0) {
    mode = DEPOSIT_BASKET_MODE.MIXED;
  }

  return {mode, eligibleItems, ineligibleItems};
};

/**
 * Sum the post-promotion line totals for an array of basket items. Falls
 * back to `priceAfterItemDiscount` → `price * quantity` → 0 so we cope with
 * partial SCAPI responses (eg. confirmation page payload that doesn't
 * include `priceAfterItemDiscount`).
 *
 * Exported as a named helper so unit tests and future "Pay now" line
 * surface ticket can re-use it.
 *
 * @param {Array} items
 * @returns {number}
 */
export const sumItemsSubtotal = (items) => {
  if (!Array.isArray(items) || items.length === 0) return 0;

  return items.reduce((acc, item) => {
    const lineTotal =
      item?.priceAfterItemDiscount ??
      (item?.price != null && item?.quantity != null ?
        item.price * item.quantity :
        0);

    return acc + (Number.isFinite(lineTotal) ? lineTotal : 0);
  }, 0);
};

/**
 * Resolve the basket's shipping total in a defensive way. Used by the
 * deposit math so it tolerates partial SCAPI payloads (eg. before the
 * shopper has picked a delivery method).
 *
 * @param {Object} basket
 * @returns {number}
 */
const getShippingTotal = (basket) => {
  if (Number.isFinite(basket?.shippingTotal)) return basket.shippingTotal;
  if (Number.isFinite(basket?.adjustedShippingTotalPrice)) return basket.adjustedShippingTotalPrice;

  return 0;
};

/**
 * Stable cart fingerprint (`productId:qty`, sorted) for cache keys and PayPal return checks.
 *
 * @param {Array<{productId?: string, quantity?: number}>} [productItems]
 * @returns {string}
 */
export const cartItemsCacheKey = (productItems) => {
  if (!productItems?.length) {
    return '';
  }

  return productItems
    .map((item) => `${item.productId ?? ''}:${item.quantity ?? 1}`)
    .sort()
    .join('|');
};

/**
 * Resolve the grand total shown to the shopper, computed identically to the
 * Order Summary so the **Pay full amount** / **Pay deposit** figures always
 * reconcile with the "Order Total" line.
 *
 * Why we don't simply trust `basket.orderTotal` mid-checkout:
 * SCAPI Shopper Baskets returns a stale (often items-only) `orderTotal` in the
 * `updateShippingMethodForShipment` PUT response — the shipping surcharge
 * (`SHIPPING_API_ADJUSTMENT`) is folded into `shippingTotal` immediately but
 * NOT into the basket-level grand total until the next basket GET. Trusting it
 * therefore makes the deposit/full amount drop the shipping cost for non-free
 * methods until something else refetches the basket (e.g. selecting a payment
 * option). So for an in-flight basket we recompute the total from constituent
 * parts (post-promotion subtotal + the freshly-applied shipping + order-level
 * adjustments + tax), exactly like `OrderSummary`. The authoritative
 * `orderTotal` is trusted only for placed orders (`orderNo` present), where it
 * is final and fully calculated (e.g. the confirmation page).
 *
 * @param {Object} basket
 * @returns {number}
 */
export const computeBasketOrderTotal = (basket) => {
  if (!basket) return 0;

  // Placed orders carry a final, fully-calculated total.
  if (basket.orderNo && Number.isFinite(basket.orderTotal)) {
    return basket.orderTotal;
  }

  const {visibleItems} = groupComplexLineItems(basket.productItems ?? []);

  const groupedSubtotal = visibleItems.length ?
    visibleItems.reduce(
      (sum, item) => sum + ((item.priceAfterItemDiscount ?? item.price ?? 0) * (item.quantity ?? 1)),
      0
    ) :
    Number.isFinite(basket.productSubTotal) ?
      basket.productSubTotal :
      Number.isFinite(basket.productTotal) ?
        basket.productTotal :
        0;

  const shipping = getShippingTotal(basket);

  const orderAdjustmentsTotal = (basket.orderPriceAdjustments ?? []).reduce(
    (sum, adj) => sum + (adj.price ?? 0),
    0
  );

  // GROSS taxation (e.g. AU GST) already bakes tax into the line prices.
  const tax = basket.taxation === 'gross' ?
    0 :
    Number.isFinite(basket.taxTotal) ? basket.taxTotal : 0;

  const computed = groupedSubtotal + shipping + orderAdjustmentsTotal + tax;

  if (Number.isFinite(computed) && computed > 0) {
    return computed;
  }

  // Last-ditch fallback when there's no line detail to compute from.
  return Number.isFinite(basket.orderTotal) ? basket.orderTotal : 0;
};

/**
 * Resolve the "full order total" displayed on the **Pay full amount** radio
 * card. Delegates to {@link computeBasketOrderTotal} so the radio, the deposit
 * math, and the Order Summary all agree on the same grand total.
 *
 * @param {Object} basket
 * @returns {number}
 */
export const getFullOrderTotal = (basket) => computeBasketOrderTotal(basket);

/**
 * Calculate the **"Pay now" amount** displayed on the deposit radio card and
 * used by Place Order to charge the right amount.
 *
 * Math:
 *   - All items eligible → `orderTotal × depositRate`.
 *   - Mixed basket → ineligible items (`c_isExcludedFromDeposit` AND, per
 *     KL-1044, in-stock floor-stock items) are paid in full at checkout
 *     (`ineligible_subtotal`); only the eligible slice is deposited
 *     (`eligible_subtotal × 0.25`). Shipping + tax are charged in full
 *     today (we don't pro-rate them; SCAPI's totals don't expose enough
 *     detail to split shipping/tax across line items, and the FSD wording
 *     ("the total will not be adjusted for the 'full price only'
 *     (excluded) products") supports keeping the side-charges intact).
 *   - All items ineligible → `orderTotal`. UI gates the deposit radio
 *     before we ever reach this branch, but we keep the math defensive so
 *     callers can call this unconditionally.
 *
 * @param {Object} basket
 * @param {Object} [productsById]
 * @param {number|null|undefined} depositRate - Decimal rate from site preference
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {number|null} Pay-now amount, or `null` when `depositRate` is invalid
 */
export const calculatePayNowOnDeposit = (
  basket,
  productsById = null,
  depositRate = null,
  inventoryIds = null
) => {
  if (!isDepositRateValid(depositRate)) return null;

  const {mode, eligibleItems, ineligibleItems} = classifyBasket(basket, productsById, inventoryIds);

  if (mode === DEPOSIT_BASKET_MODE.NO_ITEMS) return 0;
  if (mode === DEPOSIT_BASKET_MODE.ALL_INELIGIBLE) return getFullOrderTotal(basket);
  if (mode === DEPOSIT_BASKET_MODE.ALL_ELIGIBLE) return getFullOrderTotal(basket) * depositRate;

  // Mixed: ineligible items paid in full now, eligible items + shipping
  // deposited at 25%.
  const eligibleSubtotal = sumItemsSubtotal(eligibleItems);
  const ineligibleSubtotal = sumItemsSubtotal(ineligibleItems);
  const shipping = getShippingTotal(basket);

  return ineligibleSubtotal + ((eligibleSubtotal + shipping) * depositRate);
};

/**
 * Calculate the remaining-balance line ("Your remaining balance amount of
 * $X,XXX will be due 7 days before delivery.") shown under the deposit
 * radio when the shopper picks it.
 *
 * Derived as `full_order_total − pay_now_on_deposit` so the two figures
 * shown to the shopper always reconcile to the displayed order total. This
 * also sidesteps a class of subtle bugs around reconstructing the subtotal
 * from `productItems[]`: SCAPI's `productItem.priceAfterItemDiscount`
 * doesn't include Product Option contributions (e.g. KingGuard $50, which
 * lives on `optionItems[].price` and is only folded into the basket-level
 * `productSubTotal`). Computing remaining as `full − deposit` lets the
 * authoritative `basket.orderTotal` carry those nuances through.
 *
 * Returns `0` for empty baskets and for the all-ineligible mode (deposit
 * equals the full total, so there is nothing left to defer).
 *
 * @param {Object} basket
 * @param {Object} [productsById]
 * @param {number|null|undefined} depositRate - Decimal rate from site preference
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {number|null} Remaining balance, or `null` when `depositRate` is invalid
 */
export const calculateRemainingBalance = (
  basket,
  productsById = null,
  depositRate = null,
  inventoryIds = null
) => {
  if (!isDepositRateValid(depositRate)) return null;

  const {mode} = classifyBasket(basket, productsById, inventoryIds);

  if (mode === DEPOSIT_BASKET_MODE.NO_ITEMS) return 0;
  if (mode === DEPOSIT_BASKET_MODE.ALL_INELIGIBLE) return 0;

  const payNow = calculatePayNowOnDeposit(basket, productsById, depositRate, inventoryIds);

  if (payNow == null) return null;

  const remaining = getFullOrderTotal(basket) - payNow;

  return Number.isFinite(remaining) && remaining > 0 ? remaining : 0;
};

/**
 * Resolve the monetary amount to persist on the basket / order for the
 * given `paymentAmountType`. This is the value SOM / OMS reads to charge
 * the right total at place-order; persisting it removes the need for the
 * downstream to recompute the deposit split.
 *
 *   - `'full'`    → full order total (`basket.orderTotal`)
 *   - `'deposit'` → {@link calculatePayNowOnDeposit}
 *   - anything else → 0 (defensive; the provider only persists once a real
 *     selection exists, but keeping the helper total-safe lets callers
 *     invoke it unconditionally).
 *
 * @param {Object} basket
 * @param {Object|null} productsById
 * @param {string|null|undefined} paymentAmountType
 * @param {number|null|undefined} depositRate - Decimal rate from site preference
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {number}
 */
export const calculatePaymentAmount = (
  basket,
  productsById,
  paymentAmountType,
  depositRate = null,
  inventoryIds = null
) => {
  if (paymentAmountType === PAYMENT_AMOUNT_TYPE.FULL) {
    return getFullOrderTotal(basket);
  }

  if (paymentAmountType === PAYMENT_AMOUNT_TYPE.DEPOSIT) {
    const amount = calculatePayNowOnDeposit(basket, productsById, depositRate, inventoryIds);

    return amount ?? 0;
  }

  return 0;
};

/**
 * Site preference (set-of-string) holding the payment method types that are NOT
 * eligible for deposit checkout (deny-list). Each value is a Stripe
 * `payment_method_type` (e.g. `afterpay_clearpay`, `zip`, `klarna`). Exposed to
 * the storefront via `mrtClientData.js` and read on the client with
 * `useSitePreference(DEPOSIT_EXCLUDED_PAYMENT_METHOD_TYPES_PREF)`. Enforcement
 * (hiding the method + the authoritative PaymentIntent restriction) lives in the
 * Stripe layer — see `@amblique/stripe/app/utils/deposit-payment-method-types`.
 */
export const DEPOSIT_EXCLUDED_PAYMENT_METHOD_TYPES_PREF = 'depositExcludedPaymentMethodTypes';

/**
 * Is this payment-method **type** allowed when the shopper has chosen the
 * deposit option? Compares a Stripe `payment_method_type` against the
 * configured deny-list (the `depositExcludedPaymentMethodTypes` site
 * preference). Matching is case-insensitive. Returns `true` (eligible) for an
 * empty/absent deny-list so deposit stays available by default.
 *
 * @param {string} paymentMethodType - Stripe `payment_method_type` (e.g. 'klarna')
 * @param {string[]} [excludedTypes] - Configured deny-list of types
 * @returns {boolean}
 */
export const isPaymentMethodTypeEligibleForDeposit = (paymentMethodType, excludedTypes = []) => {
  if (!paymentMethodType || !Array.isArray(excludedTypes) || excludedTypes.length === 0) {
    return true;
  }

  const normalized = String(paymentMethodType).trim().toLowerCase();

  return !excludedTypes.some((type) => String(type).trim().toLowerCase() === normalized);
};

/**
 * True when the basket has a payment amount type attribute set (full or deposit).
 *
 * @param {Object|null|undefined} basket
 * @returns {boolean}
 */
export const basketHasPaymentAmountType = (basket) => Boolean(basket?.[PAYMENT_AMOUNT_TYPE_ATTR]);

/**
 * Should the Payment Amount section render? Returns false when:
 *   - the basket has no items yet (nothing to pay for),
 *   - every line item is ineligible for deposit (FSD PAYMENT-01 update +
 *     KL-1044): no deposit-eligible items means there's nothing to choose, so
 *     the section is hidden and full is the only option.
 *
 * KL-1044 — an in-stock product is NO LONGER an outright "hide the section"
 * trigger. In-stock is folded into deposit-INELIGIBILITY inside
 * `classifyBasket` / `isItemDepositEligible` (an in-stock item is treated the
 * same as a `c_isExcludedFromDeposit` item). Consequences:
 *   - A basket that is ONLY in-stock / excluded items resolves to
 *     `ALL_INELIGIBLE` → section hidden + auto-full (preserves the previous
 *     stand-alone in-stock behaviour, FSD PAYMENT-01 #1.2).
 *   - A MIXED basket (at least one deposit-eligible item alongside in-stock /
 *     excluded items) keeps the section visible; the in-stock / excluded items
 *     are charged in full at checkout while the eligible slice is deposited
 *     (see `calculatePayNowOnDeposit`).
 *
 * Pure derivation — `useCheckout()` uses this both to render the section
 * and to decide whether `paymentAmountType` must be auto-stamped to
 * `'full'`.
 *
 * @param {Object} basket
 * @param {Object|null} [productsById]
 * @param {string|undefined|null} [inventoryIds]
 * @returns {boolean}
 */
export const isPaymentAmountSectionVisible = (
  basket,
  productsById = null,
  inventoryIds = null
) => {
  if (groupComplexLineItems(basket?.productItems ?? []).visibleItems.length === 0) {
    return false;
  }

  // All-ineligible (every item in-stock and/or c_isExcludedFromDeposit) only
  // collapses to "hidden + auto-full" once the products lookup has resolved —
  // otherwise every fresh basket would briefly hide the section before
  // flashing it back in once eligibility is known.
  if (productsById) {
    const {mode} = classifyBasket(basket, productsById, inventoryIds);

    if (mode === DEPOSIT_BASKET_MODE.ALL_INELIGIBLE) return false;
  }

  return true;
};

/**
 * Charge amount for SCAPI `addPaymentInstrument` / capture — full order or deposit pay-now.
 * Shared by PayPal prepare, return handler, and any headless payment that uses
 * `c_paymentAmountType` on the basket.
 *
 * @param {Object} basket
 * @param {Object} [productsById]
 * @param {number|null|undefined} depositRate
 * @param {string|undefined|null} [inventoryIds] - Warehouse inventory ids cookie value
 * @returns {number|null}
 */
export const resolveCheckoutPaymentAmount = (
  basket,
  productsById = null,
  depositRate = null,
  inventoryIds = null
) => {
  if (!basket) {
    return null;
  }

  if (basket[PAYMENT_AMOUNT_TYPE_ATTR] === PAYMENT_AMOUNT_TYPE.DEPOSIT) {
    return calculatePayNowOnDeposit(basket, productsById, depositRate, inventoryIds);
  }

  const fullTotal = getFullOrderTotal(basket);

  return Number.isFinite(fullTotal) && fullTotal > 0 ? fullTotal : null;
};
